export type CohortPhase =
  | 'notification'
  | 'awaiting'
  | 'premigration'
  | 'migration'
  | 'postmigration'
  | 'completed'
  | 'unknown';

export type CohortSubStatus =
  | 'planned'
  | 'pending_confirmation'
  | 'confirmed'
  | 'in_progress'
  | 'error'
  | 'complete'
  | 'unknown';

export const DEFAULT_STATUS = 'Planned';
export const CONFIRMED_NO_NOTIFICATIONS_STATUS = 'Confirmed for Presync';

export const normalizeCohortStatus = (
  status?: string | null,
  enableNotifications = true,
): string => {
  if (status) return status;
  if (!enableNotifications) return CONFIRMED_NO_NOTIFICATIONS_STATUS;
  return DEFAULT_STATUS;
};

export const getCohortPhase = (status?: string | null, enableNotifications = true): CohortPhase => {
  const normalizedStatus = normalizeCohortStatus(status, enableNotifications);
  const lowerStatus = normalizedStatus.toLowerCase();

  if (
    lowerStatus === 'planned' ||
    lowerStatus.includes('notification') ||
    lowerStatus.includes('confirmed') ||
    lowerStatus.includes('pending')
  ) {
    return 'notification';
  }

  if (lowerStatus.includes('awaiting')) return 'awaiting';
  if (lowerStatus.includes('premigration')) return 'premigration';
  if (
    lowerStatus.includes('migration') &&
    !lowerStatus.includes('pre') &&
    !lowerStatus.includes('post')
  ) {
    return 'migration';
  }
  if (lowerStatus.includes('postmigration')) return 'postmigration';
  if (lowerStatus.includes('complete')) return 'completed';

  return 'unknown';
};

export const getCohortSubStatus = (
  status?: string | null,
  enableNotifications = true,
): CohortSubStatus => {
  const normalizedStatus = normalizeCohortStatus(status, enableNotifications);
  const lowerStatus = normalizedStatus.toLowerCase();

  if (lowerStatus === 'planned') return 'planned';
  if (lowerStatus.includes('planned')) return 'planned';
  if (lowerStatus.includes('pending')) return 'pending_confirmation';
  if (lowerStatus.includes('confirmed')) return 'confirmed';
  if (lowerStatus.includes('in progress')) return 'in_progress';
  if (lowerStatus.includes('error')) return 'error';
  if (lowerStatus.includes('complete')) return 'complete';

  return 'unknown';
};

export const isCohortEditable = (status?: string | null): boolean => {
  const normalizedStatus = normalizeCohortStatus(status, true);
  return normalizedStatus.toLowerCase() === 'planned';
};

export const canSendNotifications = (status?: string | null): boolean => isCohortEditable(status);

export const canSendReminders = (status?: string | null): boolean => {
  const normalizedStatus = normalizeCohortStatus(status);
  const phase = getCohortPhase(normalizedStatus);

  return (
    (phase === 'notification' || phase === 'awaiting') && !canSendNotifications(normalizedStatus)
  );
};

export const isCohortInError = (status?: string | null): boolean =>
  getCohortSubStatus(status) === 'error';

export const isCohortInProgress = (status?: string | null): boolean => {
  const phase = getCohortPhase(status);
  const subStatus = getCohortSubStatus(status);
  return (
    subStatus === 'in_progress' ||
    phase === 'awaiting' ||
    phase === 'premigration' ||
    phase === 'migration' ||
    phase === 'postmigration'
  );
};

export const isCohortCompleted = (status?: string | null): boolean =>
  getCohortPhase(status) === 'completed';

export const getStatusDisplayName = (status?: string | null, enableNotifications = true): string =>
  normalizeCohortStatus(status, enableNotifications);

export const getSiteDisplayStatus = (
  site: { upgrade_job_status?: string },
  enableNotifications = true,
): string => site.upgrade_job_status || (enableNotifications ? 'PLANNED' : 'READY');

export const FAILED_SITE_STATUSES = [
  'FAILED',
  'EXECUTION_TIMEOUT',
  'INTERNAL_ERROR',
  'INVALID_PROVIDER_RESPONSE',
  'QUEUE_TIMEOUT',
];
